/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Heading, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import FarmlandsPromotionBlockButton from './components/button';
import { IFarmlandsPromotionBlockProps, displayOnlyTo } from './farmlands-promotion-block.props.autogenerated';

export interface IContentBlockViewProps extends IFarmlandsPromotionBlockProps<{}> {
    title?: React.ReactNode;
    text?: React.ReactNode;
    image?: React.ReactNode;
    buttons?: React.ReactNode;
    className?: string;
    contentBlockContainer: IModuleProps;
    imageContainer: INodeProps;
    detailsContainer: INodeProps;
    buttonsContainer: INodeProps;
    shouldShowModule: boolean;
}
/**
 *
 * ContentCard component
 * @extends {React.PureComponent<IFarmlandsPromotionBlockProps{}>}
 */

class ContentBlock extends React.PureComponent<IFarmlandsPromotionBlockProps<{}>> {
    public render(): JSX.Element | null {
        const { heading, paragraph, image, links, displayOnlyTo, className } = this.props.config;
        const contentBlockTitle = heading && heading.text && (
            <Heading className='farmlands-promotion-block__title' headingTag={heading.tag} text={heading.text} />
        );
        const contentBlockText = paragraph && (
            <Msdyn365.RichTextComponent text={paragraph} className='farmlands-promotion-block__text' />
        );
        const contentBlockImage = image && (
            <img className='farmlands-promotion-block__image' src={image.src} alt={image.altText} />
        );
        const contentBlockButtons = links && <FarmlandsPromotionBlockButton buttons={links} />;
        const imageContainer = { className: 'farmlands-promotion-block__image-container' };
        const detailsContainer = { className: 'farmlands-promotion-block__details' };
        const buttonsContainer = { className: 'farmlands-promotion-block__buttons' };
        const isLoggedIn = this.props.context.request.user.isAuthenticated;
        const shouldShowModule = this._shouldShowTo(isLoggedIn, displayOnlyTo);
        if (!contentBlockTitle && !contentBlockText) {
            this.props.context.telemetry.error('Content block content is empty, module wont render.');
            return null;
        }

        const contentBlockviewProps = {
            ...this.props,
            title: contentBlockTitle,
            text: contentBlockText,
            image: contentBlockImage,
            buttons: contentBlockButtons,
            moduleClass: this.props.config.className,
            contentBlockContainer: {
                moduleProps: this.props,
                className: classnames('farmlands-promotion-block', className)
            },
            imageContainer: imageContainer,
            detailsContainer: detailsContainer,
            buttonsContainer: buttonsContainer,
            shouldShowModule: shouldShowModule
        };

        return this.props.renderView(contentBlockviewProps) as React.ReactElement;
    }

    private _shouldShowTo = (isLoggedIn: boolean, displayTo: displayOnlyTo): boolean => {
        if (isLoggedIn && displayTo === displayOnlyTo.loggedOutOnly) {
            return false;
        } else if (!isLoggedIn && displayTo === displayOnlyTo.loggedInOnly) {
            return false;
        }
        return true;
    };
}

export default ContentBlock;
